import { GameUpdateState, GameUpdateParentState, GameUpdate, GameUpdateListState } from "../game-update"
import { Point, equals, neighbors } from '../../math/point';
import { TacticalDisplay } from '../../display/tactical-display';
import { Ship } from '../../model/ship';
import { vector, add, Vector } from '../../math/vector';
import { Entity } from '../../model/entity';
import { World } from '../../model/world';
import { WorldTurnGameUpdate } from './world-turn';
import { EntityMotion } from '../motion/entity-motion';
import { WeaponFireStateMachine } from '../motion/weapons-fire';
import { Graphics } from 'src/app/display/graphics';
import { PointMotion } from '../motion/point-motion';
import { Rectangle } from 'src/app/math/rectangle';
import { Size } from 'src/app/math/size';
import { eq } from 'src/app/math/util';
import { WHITE_LINE, GRAY_LINE, LineStyle } from 'src/app/display/lines';
import { Weapon } from 'src/app/model/weapon';
import clone from 'immutability-helper'
import { Color } from 'src/app/display/color';

abstract class PlayerInputState extends GameUpdateState {
  protected findEnemy(world: World, p: Point): Ship | undefined {
    return world.enemies.find(enemy => equals(p, enemy.position));
  }

  protected pointerSelect(world: World): World { return world }
  protected _update(world: World, elapsed: number): World { return world }

  update(world: World, elapsed: number): World {
    if (world.selected) {
      const world0 = clone(world, {selected: {$set: false}})
      return this.pointerSelect(world0)
    }

    return this._update(world, elapsed)
  }
}

export class PlayerMoveOriginState extends PlayerInputState {
  readonly pointMotion: PointMotion

  constructor(origin: Point, destination: Point) {
    super()
    this.pointMotion = new PointMotion(origin, destination, 0.1)
  }

  update(world: World, elapsed: number): World {
    const [p, remaining] = this.pointMotion.arrive(world.grid.origin, elapsed)

    const newWorld = world.setOrgin(p)

    if (remaining === 0) {
      return newWorld
    } else {
      return this.changeState(newWorld, new PlayerFireState())
    }
  }
}

export class PlayerMoveState extends PlayerInputState {
  protected pointerSelect(world: World): World {
    const p0 = world.snap(world.pointer)

    if (world.shipCanMove(p0, world.ship) && !equals(world.ship.position, p0)) {
      const bodyMotion = new EntityMotion(world.ship.name, p0)
      const v = vector(world.ship.position, p0)
      const heading = v.angle()

      const allowedMoves = neighbors(world.grid.toGrid(p0), world.grid.toGridScalar(world.ship.speed)).
        map(p => world.grid.toWorld(p)).
        filter(p =>  world.ship.maxVehicleModel().canMove({heading: heading, position: p0}, p))

      const axis = world.grid.length/2
      const offset = new Vector(-axis, -axis)
      const ll = add(world.grid.origin, offset)

      const rectangle = new Rectangle(ll, new Size(world.grid.length))
      if (allowedMoves.some(p => !rectangle.within(p))) {
        const parentState = new GameUpdateParentState(bodyMotion, new PlayerMoveOriginState(world.grid.origin, p0))

        return this.changeState(world, parentState)
      }
      const parentState = new GameUpdateParentState(bodyMotion, new PlayerFireState())
      return this.changeState(world, parentState)
    }

    const enemy = this.findEnemy(world, p0)
    if (enemy !== undefined) {
      const weapons = world.ship.weapons.filter(weapon => weapon.canAttack(world.ship, enemy))
      if (weapons !== undefined) {
        const fireState = new WeaponsFireState(world.ship, enemy, weapons, new PlayerMoveState())

        return this.changeState(world, fireState)
      }
    }


    return this.changeState(world, new PlayerFireState())
  }

  protected _update(world: World, elapsed: number): World {
    if (world.ship.hullPoints < 0) {
      return this.changeState(world, new PlayerLostState())
    }
    return world
  }

  draw(world: World, graphics: Graphics): void {
    const tacticalDisplay = new TacticalDisplay(graphics)
    const p0 = world.snap(world.pointer)

    tacticalDisplay.shipDisplay.drawMove(world.grid, p0, world.ship, world.shipCanMove(p0, world.ship))

    const target =  this.findEnemy(world, p0) ?? world.ship.targetLock
    if (target !== undefined) {
      if (world.ship.weapons.some(weapon => weapon.canAttack(world.ship, target))) {
        tacticalDisplay.drawFireArc(world.ship, target)
        const style = new LineStyle(new Color(0xFF, 0xFF, 0xFF), 2)
        tacticalDisplay.graphics.lineStyle(WHITE_LINE)
        tacticalDisplay.graphics.lineStyle(style)
        // tacticalDisplay.graphics.drawHighlight(p0)
        // tacticalDisplay.drawCrosshair(world.ship, target)
        return
      }
    }
  }
}

class PlayerFireState extends PlayerInputState {
  protected autoLock(world: World): Ship | undefined {
    const targets = world.enemies
      .filter(ship => {
        const shipQuadrant = world.ship.quadrant(ship.position)
        return world.ship.weapons.some(weapon => weapon.arc.some(arc => arc === shipQuadrant))
      })
      .map((ship: Ship): [Ship, number] => [ship, vector(world.ship.position, ship.position).length()])
      .sort((a, b) => a[1] - b[1])

    if (targets.length > 0) {
      const closestTarget = targets[0][0]
      return closestTarget
    }
  }

  protected pointerSelect(world: World): World {
    const p0 = world.snap(world.pointer)
    const enemy = this.findEnemy(world, p0)
    if (enemy !== undefined) {
      const weapons = world.ship.weapons.filter(weapon => weapon.canAttack(world.ship, enemy))
      if (weapons !== undefined) {
        const fireState = new WeaponsFireState(world.ship, enemy, weapons, new WorldTurnGameUpdate())

        return this.changeState(world, fireState)
      }
    }

    const gameTurnInputState = new WorldTurnGameUpdate()

    this.changeState(world, gameTurnInputState)

    return world
  }

  protected _update(world: World, elapsed: number): World {
    const targetExists = world.enemies.some(target =>
      world.ship.weapons.some(weapon =>
        weapon.canAttack(world.ship, target)))

    if (!targetExists) {
      // skips player's fire turn if no valid target's exist
      return this.changeState(world, new WorldTurnGameUpdate())
    }
    return world
  }

  draw(world: World, graphics: Graphics): void {
    const tacticalDisplay = new TacticalDisplay(graphics)
    const p0 = world.snap(world.pointer)

    const targets = world.enemies
    targets.forEach(target => {
      if (world.ship.weapons.some(weapon => weapon.canAttack(world.ship, target))) {
        tacticalDisplay.graphics.lineStyle(GRAY_LINE)
        // tacticalDisplay.drawCrosshair(world.ship, target)
      }
    })

    const target =  this.findEnemy(world, p0) ?? world.ship.targetLock
    if (target !== undefined) {
      if (world.ship.weapons.some(weapon => weapon.canAttack(world.ship, target))) {
        tacticalDisplay.drawFireArc(world.ship, target)
        const style = new LineStyle(new Color(0xFF, 0xFF, 0xFF), 2)
        tacticalDisplay.graphics.lineStyle(WHITE_LINE)
        tacticalDisplay.graphics.lineStyle(style)
        // tacticalDisplay.graphics.drawHighlight(p0)
        // tacticalDisplay.drawCrosshair(world.ship, target)
        return
      }
    }
  }

}

export class WeaponsFireState extends GameUpdateListState {
  constructor(readonly source: Ship, readonly target: Ship, readonly weapons: Weapon[], nextState?: GameUpdateState) {
    super((world: World) => createWeaponFire(source, target, weapons), nextState)
  }
}

function createWeaponFire(source: Ship, target: Ship, weapons: Weapon[]): WeaponFireStateMachine[] {
  return weapons.map(weapon => createWeaponFireStateMachine(source, target, weapon))
}

function createWeaponFireStateMachine(source0: Ship, target0: Ship, weapon: Weapon): WeaponFireStateMachine {
  const lineMotion = new WeaponFireStateMachine(source0, target0, weapon)

  // lineMotion.onComplete = (world: World) => {
  //   const source = world.getShip(source0.name)
  //   const target = world.getShip(target0.name)

  //   if (source !== undefined && target !== undefined) {
  //     const targetRange = vector(target.position, source.position).length()
  //     console.log(`weapon fire damage`)

  //     const [misses, hits, auto] = weapon.damage(targetRange)
  //     console.log(`${misses}-${hits}-${auto}`)

  //     const source0 = source.setWeapon(weapon.fire(hits, auto))
  //     const world0 = world.setShip(source0)
  //     const target0 = target.damage(source0.position, hits, auto)
  //     const world1 = world0.setShip(target0)
  //     console.log(``)

  //     return world1
  //   }

  //   return world
  // }

  return lineMotion
}

class PlayerLostState extends PlayerInputState {
  draw(world: World, graphics: Graphics): void {
    const config = {
      text: "Game Over",
      style: {
        font: '32px Courier', fill: '#ffffff'
      }
    }
    var text = graphics.make.text(config);
    text.x = 265
    text.y = 250
  }
}